import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  EditAnzsicDialogData,
  EditAnzsicDialogResult,
  IndustryDivision,
  IndustryGroup,
  IndustryClass,
} from '@portal-workspace/grow-shared-library';
import { MatButtonModule } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap, switchMap, of } from 'rxjs';

@UntilDestroy()
@Component({
  templateUrl: './edit-anzsic.dialog.html',
  styleUrls: ['./edit-anzsic.dialog.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    FlexModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule
  ]
})
export class EditAnzsicDialog implements OnInit {

  formGroup: FormGroup<{
    industryDivision: FormControl<IndustryDivision | null>;
    industryGroup: FormControl<IndustryGroup | null>;
    industryClassDetail: FormControl<IndustryClass | null>;
  }>;

  formControlDivision: FormControl<IndustryDivision | null>;
  formControlGroup: FormControl<IndustryGroup | null>;
  formControlClass: FormControl<IndustryClass | null>;

  divisions: IndustryDivision[] = [];
  groups: IndustryGroup[] = [];
  classes: IndustryClass[] = [];

  isLoading = false;
  isSaving = false;

  compareWithDivision = (a: IndustryDivision | null, b: IndustryDivision | null) => a?.code === b?.code;
  compareWithGroup = (a: IndustryGroup | null, b: IndustryGroup | null) => a?.code === b?.code;
  compareWithClass = (a: IndustryClass | null, b: IndustryClass | null) => a?.code === b?.code;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EditAnzsicDialogData,
    private matDialogRef: MatDialogRef<EditAnzsicDialog, EditAnzsicDialogResult>,
    private formBuilder: FormBuilder
  ) {
    this.formControlDivision = formBuilder.control<IndustryDivision | null>(null, [Validators.required]);
    this.formControlGroup = formBuilder.control<IndustryGroup | null>(null, [Validators.required]);
    this.formControlClass = formBuilder.control<IndustryClass | null>(null, [Validators.required]);

    this.formGroup = formBuilder.group({
      industryDivision: this.formControlDivision,
      industryGroup: this.formControlGroup,
      industryClassDetail: this.formControlClass,
    });
  }

  ngOnInit(): void {
    this.isLoading = true;

    // Load divisions
    this.data.getIndustryDivisionsFn().pipe(
      untilDestroyed(this),
      tap(divisions => {
        this.divisions = divisions;
        this.initializeCurrentValues();
        this.isLoading = false;
      })
    ).subscribe();

    // Setup cascading: when division changes, load groups
    this.formControlDivision.valueChanges.pipe(
      untilDestroyed(this),
      tap(() => {
        // Reset dependent fields when division changes
        this.formControlGroup.setValue(null);
        this.formControlClass.setValue(null);
        this.groups = [];
        this.classes = [];
      }),
      switchMap(division => {
        if (division?.code) {
          return this.data.getIndustryGroupsByDivisionCodeFn(division.code);
        }
        return of([]);
      }),
      tap(groups => {
        this.groups = groups;
      })
    ).subscribe();

    // Setup cascading: when group changes, load classes
    this.formControlGroup.valueChanges.pipe(
      untilDestroyed(this),
      tap(() => {
        // Reset class when group changes
        this.formControlClass.setValue(null);
        this.classes = [];
      }),
      switchMap(group => {
        if (group?.code) {
          return this.data.getIndustryClassesByGroupCodeFn(group.code);
        }
        return of([]);
      }),
      tap(classes => {
        this.classes = classes;
      })
    ).subscribe();
  }

  private initializeCurrentValues(): void {
    const commercialEntities = this.data.application.parties?.commercialEntities ?? [];
    if (commercialEntities.length > 0) {
      // Still index 0 rather than the primary entity: the save this dialog drives writes to the same
      // position, so resolving the read by role here would let the two disagree. Recorded, not fixed.
      // The industry blocks are published as untyped JSON, hence the casts to read their codes.
      const industry = commercialEntities[0].industry;
      const division = industry?.industryDivision as { code?: string } | null | undefined;
      const group = industry?.industryGroup as { code?: string } | null | undefined;
      const classDetail = industry?.industryClassDetail as { code?: string } | null | undefined;

      // Set current division
      if (division) {
        const currentDivision = this.divisions.find(d => d.code === division?.code);
        if (currentDivision) {
          // Temporarily unsubscribe to prevent cascading reset
          this.formControlDivision.setValue(currentDivision, { emitEvent: false });

          // Load groups for current division
          this.data.getIndustryGroupsByDivisionCodeFn(currentDivision.code).pipe(
            untilDestroyed(this),
            tap(groups => {
              this.groups = groups;

              // Set current group
              if (group) {
                const currentGroup = groups.find(g => g.code === group?.code);
                if (currentGroup) {
                  this.formControlGroup.setValue(currentGroup, { emitEvent: false });

                  // Load classes for current group
                  this.data.getIndustryClassesByGroupCodeFn(currentGroup.code).pipe(
                    untilDestroyed(this),
                    tap(classes => {
                      this.classes = classes;

                      // Set current class
                      if (classDetail) {
                        const currentClass = classes.find(c => c.code === classDetail?.code);
                        if (currentClass) {
                          this.formControlClass.setValue(currentClass, { emitEvent: false });
                        }
                      }
                    })
                  ).subscribe();
                }
              }
            })
          ).subscribe();
        }
      }
    }
  }

  onCancel(): void {
    this.matDialogRef.close({ updated: false });
  }

  onSubmit(): void {
    if (this.formGroup.valid && !this.isSaving) {
      this.isSaving = true;

      const division = this.formControlDivision.value!;
      const group = this.formControlGroup.value!;
      const classDetail = this.formControlClass.value!;

      this.data.updateApplicationAnzsicFn({
        applicationId: this.data.application.ApplicationId,
        industryDivision: division,
        industryGroup: group,
        industryClassDetail: classDetail,
      }).pipe(
        untilDestroyed(this),
        tap({
          next: (response) => {
            this.isSaving = false;
            if (response.status) {
              this.matDialogRef.close({ updated: true });
            }
          },
          error: (error) => {
            this.isSaving = false;
            console.error('Failed to update ANZSIC', error);
          }
        })
      ).subscribe();
    }
  }
}

